import {
  getAuth,
  GoogleAuthProvider,
  GithubAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification as firebaseSendEmailVerification,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  updateProfile,
  signOut,
} from "firebase/auth";

import { db } from "../firebaseConfig";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";

const API_BASE_URL = import.meta.env.VITE_APP_API_URL || "https://auth.ezron.ai";

function getActionCodeSettings(path = "/auth/action") {
  if (typeof window === "undefined") {
    return undefined;
  }

  return {
    url: `${window.location.origin}${path}`,
    handleCodeInApp: false,
  };
}

function usesPasswordProvider(firebaseUser) {
  return !!firebaseUser?.providerData?.some((provider) => provider?.providerId === "password");
}

export function needsEmailVerification(firebaseUser) {
  return usesPasswordProvider(firebaseUser) && !firebaseUser?.emailVerified;
}

/**
 * Posts a fresh Firebase ID token to the server so it sets the HttpOnly cookie.
 * Always forces a token refresh to avoid posting an expired token.
 */
async function syncCookieSession(firebaseUser) {
  const token = await firebaseUser.getIdToken(/* forceRefresh= */ true);
  const res = await fetch(`${API_BASE_URL}/login`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Session sync failed (${res.status})${text ? ": " + text : ""}`);
  }
}

// 🔵 Hook for social login
export const useSocialLogin = () => {
  const socialLoginUser = async (provider) => {
    const auth = getAuth();
    const result = await signInWithPopup(auth, provider);
    await syncCookieSession(result.user);
    return result.user;
  };

  return {
    loginWithGoogle: () => socialLoginUser(new GoogleAuthProvider()),
    loginWithGithub: () => socialLoginUser(new GithubAuthProvider()),
  };
};

// 🔵 Function for email/password login
export const loginUser = async (email, password) => {
  const auth = getAuth();
  const credential = await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
  const requiresEmailVerification = needsEmailVerification(credential.user);

  if (!requiresEmailVerification) {
    await syncCookieSession(credential.user);
  }

  return {
    user: credential.user,
    requiresEmailVerification,
  };
};

// 🔴 Function for logging out
export const logoutUser = async () => {
  try {
    const auth = getAuth();
    await signOut(auth);
    await fetch(`${API_BASE_URL}/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    window.location.href = "/login";
  } catch (error) {
    console.error("Logout failed:", error);
  }
};

// 🔵 Sign out without redirect (used for session isolation, e.g. portal login)
export const signOutQuietly = async () => {
  try {
    const auth = getAuth();
    await signOut(auth);
    await fetch(`${API_BASE_URL}/logout`, { method: "POST", credentials: "include" }).catch(() => {});
  } catch (error) {
    console.error("Quiet sign-out failed:", error);
  }
};

// 🔵 Register a new user with email/password
export const registerUser = async (email, password, displayName) => {
  const auth = getAuth();
  const normalizedEmail = email.trim().toLowerCase();
  const credential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
  if (displayName) {
    await updateProfile(credential.user, { displayName });
  }
  await firebaseSendEmailVerification(credential.user, getActionCodeSettings("/auth/action"));
  // Create a users/{uid} record — every user can use both Tenant and PM mode,
  // no approval needed. accountStatus only changes if an admin pauses them.
  const userRef = doc(db, "users", credential.user.uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) {
    await setDoc(userRef, {
      uid: credential.user.uid,
      email: normalizedEmail,
      displayName: displayName || "",
      accountStatus: "active",
      createdAt: serverTimestamp(),
    });
  }
  return credential.user;
};

// 🔵 Send a password-reset email
export const resetPassword = async (email) => {
  const auth = getAuth();
  await sendPasswordResetEmail(auth, email.trim().toLowerCase(), getActionCodeSettings("/auth/action"));
};

export const changePassword = async (currentPassword, newPassword) => {
  const auth = getAuth();
  const firebaseUser = auth.currentUser;

  if (!firebaseUser?.email || !usesPasswordProvider(firebaseUser)) {
    throw new Error("Password changes are only available for email and password accounts.");
  }

  const credential = EmailAuthProvider.credential(firebaseUser.email, currentPassword);
  await reauthenticateWithCredential(firebaseUser, credential);
  await updatePassword(firebaseUser, newPassword);
};

export const hasPasswordProvider = (firebaseUser) => usesPasswordProvider(firebaseUser);

export const sendVerificationEmail = async (firebaseUser) => {
  if (!firebaseUser) {
    throw new Error("No authenticated user available for email verification.");
  }

  await firebaseSendEmailVerification(firebaseUser, getActionCodeSettings("/auth/action"));
};

export const syncCurrentUserSession = async () => {
  const auth = getAuth();
  if (!auth.currentUser || needsEmailVerification(auth.currentUser)) {
    return false;
  }

  await syncCookieSession(auth.currentUser);
  return true;
};


/**
 * Initializes a user's progress in Firestore if not already created.
 */
export async function initializeUserProgress(userId) {
  const userDocRef = doc(db, "userProgress", userId);
  const docSnap = await getDoc(userDocRef);

  if (!docSnap.exists()) {
    await setDoc(userDocRef, {
      tourCompleted: false,
      apiKeyProvided: false,
      dataSourceConnected: false,
      agentCreated: false
    });
  }
}

/**
 * Retrieves the user's onboarding progress.
 */
export async function getUserProgress(userId) {
  const userDocRef = doc(db, "userProgress", userId);
  const docSnap = await getDoc(userDocRef);
  
  return docSnap.exists() ? docSnap.data() : null;
}

/**
 * Updates a specific progress step for a user.
 */
export async function updateUserProgress(userId, stepKey, value) {
  const userDocRef = doc(db, "userProgress", userId);
  await updateDoc(userDocRef, {
    [stepKey]: value
  });
}
